import { useCallback, useMemo, useState } from "react";
import { aggregateStagedEdits, nextTempEntryId } from "./stagedEdits";
const EMPTY_STAGED_EDITS = { parent: {}, entryChanges: [] };
/**
 * Owns the local staging session for a public-lorebook edit: the chronological log of parent and
 * entry changes the user has made but not yet submitted for moderation. Live lorebook data is left
 * untouched until approval — this session is the only place the pending edits exist client-side.
 *
 * Consumers stage changes as the user edits (an entry modal, the details form), read the derived
 * `pendingLorebookData` snapshot to submit via `submitLorebookModeration`, and `reset()` once the
 * submit succeeds (or the user discards).
 *
 * The chronological log is intentionally append-only; {@link aggregateStagedEdits} collapses it into
 * at most one op per entry, so staging the same entry repeatedly is safe and self-merging.
 */
export default function useLorebookStagedEdits() {
    const [stateStaged, setStateStaged] = useState(EMPTY_STAGED_EDITS);
    const appendChange = useCallback((change) => {
        setStateStaged((prev) => ({ ...prev, entryChanges: [...prev.entryChanges, change] }));
    }, []);
    /**
     * Stage the creation of a new entry. Returns the temporary id assigned to it, which the caller
     * keeps so later edits/deletes of the same not-yet-created entry reference the same staged op.
     */
    const stageEntryCreate = useCallback((entryData) => {
        const entryId = nextTempEntryId();
        appendChange({ kind: "create", entryId, data: entryData });
        return entryId;
    }, [appendChange]);
    /** Stage an update to an existing entry, or to a staged (not-yet-created) entry by its temp id. */
    const stageEntryUpdate = useCallback((entryId, entryData) => {
        appendChange({ kind: "update", entryId, data: entryData });
    }, [appendChange]);
    /** Stage a delete. A staged-create + delete of the same temp id cancels out during aggregation. */
    const stageEntryDelete = useCallback((entryId) => {
        appendChange({ kind: "delete", entryId });
    }, [appendChange]);
    /**
     * Stage parent-field edits. Merged over any previously staged parent fields, so only the fields
     * present in `change` are updated. An explicit empty string clears a field; `undefined` leaves it
     * unstaged (only staged fields reach the moderation payload).
     */
    const stageParentChange = useCallback((change) => {
        setStateStaged((prev) => ({ ...prev, parent: { ...prev.parent, ...change } }));
    }, []);
    /** Drop the entire staging session (after a successful submit, or an explicit discard). */
    const resetStagedEdits = useCallback(() => {
        setStateStaged(EMPTY_STAGED_EDITS);
    }, []);
    // Aggregated moderation snapshot for submission. Recomputed from the log on every change.
    const pendingLorebookData = useMemo(() => aggregateStagedEdits(stateStaged), [stateStaged]);
    // At most one op per entry id, keyed for the list-overlay layer to look up an entry's staged op.
    const stagedOpByEntryId = useMemo(() => {
        const map = new Map();
        for (const op of pendingLorebookData.entry_ops ?? []) {
            map.set(op.entry_id, op);
        }
        return map;
    }, [pendingLorebookData]);
    const hasStagedChanges = useMemo(() => (pendingLorebookData.entry_ops?.length ?? 0) > 0 || Object.keys(stateStaged.parent).length > 0, [pendingLorebookData, stateStaged.parent]);
    return {
        pendingLorebookData,
        hasStagedChanges,
        stagedOpByEntryId,
        stageEntryCreate,
        stageEntryUpdate,
        stageEntryDelete,
        stageParentChange,
        resetStagedEdits,
    };
}
